import { Grid } from '@mui/material';
import { AddButton } from '../../../../components/simple/AllButtons';
import DateBasedAttendanceComponent from '../../../../components/attendanceComponent/dateBasedAttendanceComponent';
import UserBasedAttendanceComponent from '../../../../components/attendanceComponent/userBasedAttendanceComponent';
import { FC, useEffect, useCallback, useMemo } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import StudentAttendanceTracker from './studentsAttendanceTracker';
import {
  useStudentAttendanceActions,
  useStudentAttendanceState,
  ATTENDANCE_VIEW_MODE,
} from '../../contexts/studentAttendance.context';
import { useTranslation } from '../../../../../hooks/useTranslationHook';
import ControlledDateRangePicker from '../../../../components/controlledComponents/datePicker/datePicker.component';
import { PermissionGate } from '../../../../../components/rbac/PermissionGate';
import { RbacDeniedRedirect } from '../../../../../components/rbac/RbacDeniedRedirect';
import { TAttendanceByUserResponse } from '../../../../../apis/__shared/attendance.interfaces';
import { useStudentFiltersState } from '../../contexts/studentFilters.context';
import { useStudentsService } from '../../../../../apis/students/useStudentsService';
import { buildStudentAttendanceByUserQueryKey } from '../../hooks/useStudentAttendanceByUserHook';

interface StudentAttendanceProps {
  // view props goes here
}

const StudentAttendance: FC<StudentAttendanceProps> = () => {
  const { translateText } = useTranslation();
  const filters = useStudentFiltersState();
  const {
    studentAttendanceRecords,
    studentAttendanceByUserRecords,
    studentAttendanceAppState: {
      isAttendanceTrackerOpen,
      dateRange,
      viewMode,
      userParams,
      grade,
      section,
      selectedStudentId,
    },
  } = useStudentAttendanceState();
  const {
    setIsAttendanceTrackerOpen,
    setIsEditMode,
    setSelectedAttendance,
    setDateRange,
    setSelectedStudentId,
    setViewMode,
    setGradeSection,
  } = useStudentAttendanceActions();
  const { getStudentAttendanceByUser } = useStudentsService();
  const queryClient = useQueryClient();

  // Sync grade/section from filters context to attendance context
  useEffect(() => {
    const gradeValue = filters.grade !== 'select' ? filters.grade : undefined;
    const sectionValue = filters.section !== 'select' ? filters.section : undefined;
    if (grade !== gradeValue || section !== sectionValue) {
      setGradeSection(gradeValue, sectionValue);
    }
  }, [filters.grade, filters.section, grade, section, setGradeSection]);

  // Stable Dates + filter-derived ids must match StudentAttendanceProvider's useStudentAttendanceByUserHook.
  const classIdForAttendance =
    filters.grade !== 'select' ? filters.grade : undefined;
  const sectionIdForAttendance =
    filters.section !== 'select' ? filters.section : undefined;

  const fromDate = useMemo(
    () =>
      dateRange.fromDate
        ? dateRange.fromDate.startOf('day').toDate()
        : undefined,
    [dateRange.fromDate]
  );
  const toDate = useMemo(
    () =>
      dateRange.toDate ? dateRange.toDate.endOf('day').toDate() : undefined,
    [dateRange.toDate]
  );

  // Row click: update context then hydrate React Query cache (same key as provider) — no duplicate storm from unstable callback refs.
  const handleFetchUserAttendance = useCallback(
    async (userId: string): Promise<TAttendanceByUserResponse> => {
      setSelectedStudentId(userId);
      setViewMode(ATTENDANCE_VIEW_MODE.BY_USER);
      return queryClient.ensureQueryData({
        queryKey: buildStudentAttendanceByUserQueryKey(
          userParams,
          classIdForAttendance,
          sectionIdForAttendance,
          fromDate,
          toDate,
          userId
        ),
        queryFn: () =>
          getStudentAttendanceByUser(
            userParams,
            classIdForAttendance,
            sectionIdForAttendance,
            fromDate,
            toDate,
            userId
          ),
      });
    },
    [
      queryClient,
      setSelectedStudentId,
      setViewMode,
      getStudentAttendanceByUser,
      userParams,
      classIdForAttendance,
      sectionIdForAttendance,
      fromDate,
      toDate,
    ]
  );

  const handleDateRangeChange = (fromDate: any, toDate: any) => {
    setDateRange(fromDate, toDate);
  };

  const handleViewChange = (isDetailsView: boolean) => {
    if (!isDetailsView && viewMode === ATTENDANCE_VIEW_MODE.BY_USER) {
      setViewMode(ATTENDANCE_VIEW_MODE.BY_DATE);
      setSelectedStudentId(undefined);
    }
  };

  const handleBackToDateView = () => {
    setViewMode(ATTENDANCE_VIEW_MODE.BY_DATE);
    setSelectedStudentId(undefined);
  };

  const onTrackAttendanceClickHandler = () => {
    setIsAttendanceTrackerOpen(true);
  };

  const onEditAttendanceClickHandler = (data: any) => {
    setIsAttendanceTrackerOpen(true);
    setIsEditMode(true);
    setSelectedAttendance(data);
  };

  const isDetailsView = viewMode === ATTENDANCE_VIEW_MODE.BY_USER;

  return (
    <PermissionGate
      module="students"
      submodule="attendance"
      permission="read"
      fallback={<RbacDeniedRedirect />}
    >
      <Grid 
        container 
        spacing={{ xs: 1, sm: 2 }}
        sx={{
          p: { xs: 1, sm: 2 },
        }}
        data-testid="student-attendance-container"
      >
        {!isDetailsView && (
          <Grid
            item
            xs={12}
            sx={{
              display: 'flex',
              flexDirection: { xs: 'column', sm: 'row' },
              alignItems: { xs: 'stretch', sm: 'center' },
              justifyContent: 'space-between',
              gap: { xs: 1, sm: 2 },
              mb: { xs: 1, sm: 0 },
            }}
            data-testid="student-attendance-header-container"
          >
            <ControlledDateRangePicker
              id="student-attendance-date-range"
              name="dateRange"
              placeholder={`${translateText('students.attendance.fromDate')} - ${translateText('students.attendance.toDate')}`}
              value={[dateRange.fromDate, dateRange.toDate]}
              onChange={handleDateRangeChange}
              isGridItem={false}
              isClearable
              dataTestId="student-attendance-date-range-picker"
            />
            <PermissionGate
              module="students"
              submodule="attendance"
              permission="write"
            >
              <AddButton
                label={translateText('students.attendance.trackAttendance')}
                onClickHandler={onTrackAttendanceClickHandler}
                data-testid="student-attendance-add-button"
                sx={{
                  width: { xs: '100%', sm: 'auto' },
                }}
              />
            </PermissionGate>
          </Grid>
        )}
        <Grid item xs={12}>
          {viewMode === ATTENDANCE_VIEW_MODE.BY_USER &&
          selectedStudentId &&
          studentAttendanceByUserRecords ? (
            <UserBasedAttendanceComponent
              atendanceRecords={studentAttendanceByUserRecords}
              onEditAttendanceClickHandler={onEditAttendanceClickHandler}
              onFetchUserAttendance={handleFetchUserAttendance}
              userId={selectedStudentId}
              onBackToDateView={handleBackToDateView}
              hideBackButton={false}
            />
          ) : (
            <DateBasedAttendanceComponent
              atendanceRecords={studentAttendanceRecords}
              onEditAttendanceClickHandler={onEditAttendanceClickHandler}
              onViewChange={handleViewChange}
              onFetchUserAttendance={handleFetchUserAttendance}
            />
          )}
        </Grid>
        <StudentAttendanceTracker
          isOpen={isAttendanceTrackerOpen}
          onCloseAttendanceTrackerModal={() => {
            setIsAttendanceTrackerOpen(false);
          }}
        />
      </Grid>
    </PermissionGate>
  );
};

export default StudentAttendance;
